import { useState, useEffect } from 'react';
import { PageHeader, Badge, type ToneType } from './shared';
import { Icon } from './ui/icons';

interface Tool {
  id: string;
  name: string;
  kind: string;
  category: string;
  status: 'connected' | 'available' | 'error';
  version: string;
  owner: string;
  description: string;
  capabilities: string[];
  scope: string;
  lastCall?: string;
  calls7d: number;
}

const mockTools: Tool[] = [
  {
    id: 'github.repo',
    name: 'GitHub',
    kind: 'mcp',
    category: 'VCS',
    status: 'connected',
    version: 'mcp-server v0.4.1',
    owner: 'Léa Martin',
    description: 'Read/write to repos, branches, PRs, issues, file content. Scoped to the installed App permissions.',
    capabilities: ['read-files', 'open-pr', 'comment', 'create-branch', 'create-issue', 'list-checks'],
    scope: 'All 12 installed repos',
    lastCall: '2026-05-19T07:42:00Z',
    calls7d: 4128
  },
  {
    id: 'aws.iam',
    name: 'AWS IAM',
    kind: 'mcp',
    category: 'Cloud',
    status: 'connected',
    version: 'mcp-server v1.2.0',
    owner: 'Léa Martin',
    description: 'Manage IAM users, roles, policies. Read posture; rotate/disable credentials. Read-only by default; write actions require an approval ceremony.',
    capabilities: ['read-policy', 'disable-access-key', 'create-role', 'attach-policy'],
    scope: 'production / staging accounts',
    lastCall: '2026-05-19T06:00:00Z',
    calls7d: 47
  },
  {
    id: 'aws.kms',
    name: 'AWS KMS',
    kind: 'mcp',
    category: 'Cloud',
    status: 'connected',
    version: 'mcp-server v1.0.4',
    owner: 'Léa Martin',
    description: 'Inspect / create KMS keys, rotate, manage key policies.',
    capabilities: ['list-keys', 'rotate-key', 'update-policy'],
    scope: 'production account',
    lastCall: '2026-05-17T15:00:00Z',
    calls7d: 6
  },
  {
    id: 'slack',
    name: 'Slack',
    kind: 'mcp',
    category: 'Messaging',
    status: 'connected',
    version: 'mcp-server v0.8.0',
    owner: 'Tom Holland',
    description: 'Post messages and threads, attach evidence files. Used by the Auto-remediator and Acceptance assistant.',
    capabilities: ['post-message', 'open-thread', 'upload-file'],
    scope: '#cylift-alerts, #sec-on-call',
    lastCall: '2026-05-19T07:42:00Z',
    calls7d: 412
  },
  {
    id: 'jira',
    name: 'Jira',
    kind: 'mcp',
    category: 'Tracking',
    status: 'connected',
    version: 'mcp-server v0.6.2',
    owner: 'Tom Holland',
    description: 'Create / transition issues, link to PRs. Used when an agent can\'t auto-fix (e.g. IdP changes).',
    capabilities: ['create-issue', 'transition', 'comment', 'link-pr'],
    scope: 'Project SEC, OPS',
    lastCall: '2026-05-18T18:00:00Z',
    calls7d: 18
  },
  {
    id: 'terraform-cloud',
    name: 'Terraform Cloud',
    kind: 'mcp',
    category: 'Infra',
    status: 'available',
    version: 'mcp-server v0.3.0',
    owner: 'Léa Martin',
    description: 'Plan / apply Terraform configs, read workspace state. Apply requires human approval.',
    capabilities: ['read-state', 'plan', 'apply (gated)'],
    scope: 'Not connected yet',
    lastCall: undefined,
    calls7d: 0
  },
  {
    id: 'pagerduty',
    name: 'PagerDuty',
    kind: 'mcp',
    category: 'Incidents',
    status: 'available',
    version: 'mcp-server v0.5.0',
    owner: 'Tom Holland',
    description: 'Trigger / resolve incidents for criticals that exceed acceptance windows.',
    capabilities: ['trigger-incident', 'resolve', 'snooze'],
    scope: 'Not connected yet',
    lastCall: undefined,
    calls7d: 0
  }
];

const statusTone: Record<string, ToneType> = {
  connected: 'ok',
  available: 'muted',
  error: 'crit'
};

const categoryColors: Record<string, string> = {
  'VCS': '#0969DA',
  'Cloud': '#FF9500',
  'Messaging': '#7856D9',
  'Tracking': '#0369A1',
  'Infra': '#059669',
  'Incidents': '#DC2626'
};

function formatTimeAgo(timestamp: string): string {
  const now = new Date();
  const eventTime = new Date(timestamp);
  const diffMs = now.getTime() - eventTime.getTime();
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

export default function Tools() {
  const [tools, setTools] = useState<Tool[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [search, setSearch] = useState<string>('');

  useEffect(() => {
    const loadTools = async () => {
      setLoading(true);
      await new Promise(resolve => setTimeout(resolve, 500));
      setTools(mockTools);
      setLoading(false);
    };
    
    loadTools();
  }, []);

  const filteredTools = tools.filter(tool => {
    const matchesFilter = filter === 'all' || 
      tool.status === filter ||
      tool.category.toLowerCase() === filter.toLowerCase();
    
    const matchesSearch = search === '' || 
      tool.name.toLowerCase().includes(search.toLowerCase()) ||
      tool.description.toLowerCase().includes(search.toLowerCase()) ||
      tool.category.toLowerCase().includes(search.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });

  const toolStats = {
    total: tools.length,
    connected: tools.filter(t => t.status === 'connected').length,
    available: tools.filter(t => t.status === 'available').length,
    totalCalls: tools.reduce((sum, t) => sum + t.calls7d, 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex items-center gap-2 text-ink/60">
          <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
          </svg>
          <span className="font-display text-[16px]">Loading tools...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title="Connected Tools"
        subtitle="MCP tools that enable automated security and compliance workflows across your infrastructure."
        right={
          <div className="flex items-center gap-3">
            <button className="h-10 px-4 rounded-[10px] border border-[#F3E4C8] bg-white font-display font-bold text-[14px] inline-flex items-center gap-2 hover:border-ink/40">
              <Icon name="download" size={14} stroke="#121111" /> Export Config
            </button>
            <button className="h-10 px-4 rounded-[10px] bg-[#FA500E] text-white font-display font-bold text-[14px] inline-flex items-center gap-2 hover:opacity-90">
              <Icon name="plus" size={14} stroke="white" /> Connect Tool
            </button>
          </div>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Total Tools</div>
          <div className="font-display font-bold text-ink mt-0.5 text-[24px]">{toolStats.total}</div>
        </div>
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Connected</div>
          <div className="font-display font-bold mt-0.5 text-[24px]" style={{ color: '#159D4C' }}>{toolStats.connected}</div>
        </div>
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Available</div>
          <div className="font-display font-bold mt-0.5 text-[24px]" style={{ color: '#6B7280' }}>{toolStats.available}</div>
        </div>
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">7-Day Calls</div>
          <div className="font-display font-bold mt-0.5 text-[24px]" style={{ color: '#0A69DA' }}>{toolStats.totalCalls.toLocaleString()}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Icon name="search" size={16} className="text-ink/40" />
          <input
            type="text"
            placeholder="Search tools..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="font-display text-[14px] text-ink placeholder-ink/40 border-none outline-none bg-transparent"
          />
        </div>
        
        <div className="flex items-center gap-2 border-l border-gray-200 pl-4">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="font-display text-[14px] text-ink border border-[#F3E4C8] rounded-[6px] px-3 py-2 bg-white"
          >
            <option value="all">All Tools</option>
            <option value="connected">Connected</option>
            <option value="available">Available</option>
            <option value="VCS">VCS</option>
            <option value="Cloud">Cloud</option>
            <option value="Messaging">Messaging</option>
            <option value="Tracking">Tracking</option>
          </select>
        </div>
      </div>

      {/* Tools Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {filteredTools.map(tool => (
          <div key={tool.id} className="rounded-[10px] border border-[#F3E4C8] bg-white p-5 flex flex-col gap-3">
            <div className="flex items-start justify-between">
              <div 
                className="h-10 w-10 rounded-[10px] grid place-items-center border"
                style={{ 
                  backgroundColor: `${categoryColors[tool.category]}1a`, 
                  borderColor: `${categoryColors[tool.category]}33` 
                }}
              >
                <Icon name="package" size={18} stroke={categoryColors[tool.category]} />
              </div>
              <Badge tone={statusTone[tool.status]} dot>{tool.status}</Badge>
            </div>

            <div>
              <div className="font-display font-bold text-ink text-[16px]">{tool.name}</div>
              <div className="font-display text-[12px] text-ink/55 mt-0.5">{tool.category}</div>
            </div>

            <p className="font-display text-[13px] text-ink/70 flex-1 line-clamp-3">{tool.description}</p>

            <div className="space-y-2">
              <div>
                <span className="font-display font-semibold text-[12px] text-ink/70">Capabilities:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {tool.capabilities.slice(0, 3).map(capability => (
                    <span key={capability} className="inline-flex items-center px-2 py-1 rounded-[4px] bg-[#DDF4FF] text-[#0A69DA] font-display text-[10px] font-semibold">
                      {capability}
                    </span>
                  ))}
                  {tool.capabilities.length > 3 && (
                    <span className="inline-flex items-center px-2 py-1 rounded-[4px] bg-[#F7EDDB] text-[#CA9401] font-display text-[10px] font-semibold">
                      +{tool.capabilities.length - 3} more
                    </span>
                  )}
                </div>
              </div>

              <div>
                <span className="font-display font-semibold text-[12px] text-ink/70">Scope:</span>
                <p className="font-display text-[12px] text-ink/65 mt-1 line-clamp-1">{tool.scope}</p>
              </div>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-[#F7EDDB]">
              <div className="text-left">
                <div className="font-data text-[12px] text-ink/55">
                  {tool.calls7d.toLocaleString()} calls (7d)
                </div>
                {tool.lastCall && (
                  <div className="font-data text-[11px] text-ink/45 mt-0.5">
                    Last: {formatTimeAgo(tool.lastCall)}
                  </div>
                )}
              </div>
              <button className={`font-display text-[13px] font-bold ${
                tool.status === 'connected' ? 'text-ink/50' : 'text-[#FA500E] hover:underline'
              }`}>
                {tool.status === 'connected' ? 'Configure' : 'Connect →'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {!filteredTools.length && (
        <div className="text-center py-12">
          <Icon name="package" size={48} className="text-ink/40 mx-auto mb-4" />
          <h3 className="font-display font-bold text-ink text-[18px] mb-2">No tools found</h3>
          <p className="font-display text-[14px] text-ink/60">Try adjusting your search or filter criteria.</p>
        </div>
      )}
    </div>
  );
}